import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Text,
  useToast,
  VStack,
  HStack,
  Link,
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { IssueDetail } from "models/issue";
import { useUserDetailQuery, useCurrentUserQuery } from "features/User/hooks";
import { ResolutionInfo } from "models/resolutions";
import { GrayTableButton } from "components/Button/TableButton";
import { useMessageTemplateListQuery } from "features/Resources/MessageTemplate/hooks";
import { useMemo, useRef, useState } from "react";
import { generatePath, useNavigate } from "react-router";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import { MessageTemplate } from "features/Resources/MessageTemplate/models";
import apiClient from "services/ApiClient";
import { apiMessageTemplateById } from "helpers/url/api/v2";
import { createQueryString } from "helpers/queryString";
import { useMessageListQuery } from "features/Resources/Message/hooks";

export function deactivatedSenderIssueType(_: IssueDetail) {
  return "Deactivated Sender";
}

export function expandedDeactivatedSenderIssueType(issue: IssueDetail) {
  if (issue?.user?.fullName) {
    return `Message Sender ${issue?.user?.fullName} Deactivated`;
  }
  return deactivatedSenderIssueType(issue);
}

function BulkUpdateConfirmationModal({
  userId,
  newSenderId,
  newSenderName,
  templatesCount,
  messagesCount,
  isOpen,
  onClose,
}: {
  userId: string;
  newSenderId: string | null;
  newSenderName: string;
  templatesCount: number;
  messagesCount: number;
  isOpen: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirmUpdate = async () => {
    setIsLoading(true);
    try {
      await apiClient.post("/api/message-templates/bulk-update-sender", {
        old_sender_id: userId,
        new_sender_id: newSenderId,
        update_messages: true,
      });

      toast({
        title: "Templates and messages updated",
        description: `${templatesCount} template${templatesCount !== 1 ? "s" : ""} and ${messagesCount} message${messagesCount !== 1 ? "s" : ""} updated to ${newSenderName}`,
        status: "success",
        duration: 3000,
      });

      onClose();
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to update templates and messages",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Update Message Sender
          </AlertDialogHeader>

          <AlertDialogBody>
            This will update the sender to
            {" "}
            <strong>{newSenderName}</strong>
            {" "}
            for
            {" "}
            <strong>{templatesCount}</strong>
            {" "}
            template
            {templatesCount !== 1 ? "s" : ""}
            {" "}
            and
            {" "}
            <strong>{messagesCount}</strong>
            {" "}
            pending
            {" "}
            message
            {messagesCount !== 1 ? "s" : ""}
            .
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} isDisabled={isLoading}>
              Nevermind
            </Button>
            <Button colorScheme="blue" onClick={handleConfirmUpdate} ml={3} isLoading={isLoading}>
              I Understand
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

function FailedMessagesModal({
  template,
  isOpen,
  onClose,
  newSenderName,
}: {
  template: MessageTemplate;
  isOpen: boolean;
  onClose: () => void;
  newSenderName: string;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const queryString = createQueryString({
    filters: [
      { fieldName: "templateId", value: template?.id, operator: "=" },
      { fieldName: "status", value: ["delivered", "skipped", "removed"], operator: "not_in" },
    ],
  });
  const { data: messages = [] } = useMessageListQuery(queryString);

  const failedMessages = useMemo(() => messages.filter((msg: any) => msg.status === "failed"), [messages]);

  const handleDoNothing = () => {
    onClose();
  };

  const handleSendAllFailed = async () => {
    setIsLoading(true);
    try {
      // TODO: Call backend endpoint to force send all failed messages for this template
      // POST /api/message-templates/:id/force_send_failed

      toast({
        title: "Messages sent",
        description: `${failedMessages.length} failed message${failedMessages.length > 1 ? "s" : ""} sent with new sender`,
        status: "success",
        duration: 3000,
      });

      onClose();
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to send messages",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewMessages = () => {
    navigate(`${PEOPLE_TEAM_ROUTES.messages.index}?templateId=${template.id}`);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Template Updated -
          {" "}
          {template.name}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Text mb={4}>
            {messages.length}
            {" "}
            message
            {messages.length !== 1 ? "s have" : " has"}
            {" "}
            been updated to send from
            {" "}
            {newSenderName}
            .
          </Text>
          {failedMessages.length > 0 ? (
            <>
              <Text mb={4} fontWeight="medium">
                {failedMessages.length}
                {" "}
                of these message
                {failedMessages.length > 1 ? "s" : ""}
                {" "}
                previously failed to send:
              </Text>
              <VStack spacing={3} align="stretch">
                <GrayTableButton
                  onClick={handleSendAllFailed}
                  isLoading={isLoading}
                >
                  Send all failed messages now
                </GrayTableButton>
                <GrayTableButton
                  onClick={handleDoNothing}
                  isDisabled={isLoading}
                >
                  Skip for now
                </GrayTableButton>
                <GrayTableButton
                  onClick={handleViewMessages}
                  isDisabled={isLoading}
                >
                  View all messages
                </GrayTableButton>
              </VStack>
            </>
          ) : (
            <VStack spacing={3} align="stretch">
              <GrayTableButton
                onClick={handleDoNothing}
              >
                Close
              </GrayTableButton>
              <GrayTableButton
                onClick={handleViewMessages}
              >
                View all messages
              </GrayTableButton>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function IndividualUpdateConfirmationAlert({
  template,
  senderId,
  senderName,
  messagesCount,
  isOpen,
  onClose,
  onConfirm,
}: {
  template: MessageTemplate;
  senderId: string | null;
  senderName: string;
  messagesCount: number;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Update Message Sender
          </AlertDialogHeader>

          <AlertDialogBody>
            This will update the sender to
            {" "}
            <strong>{senderName}</strong>
            {" "}
            for the template
            {" "}
            <strong>{template.name}</strong>
            {" "}
            and
            {" "}
            <strong>{messagesCount}</strong>
            {" "}
            pending
            {" "}
            message
            {messagesCount !== 1 ? "s" : ""}
            .
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose}>
              Nevermind
            </Button>
            <Button colorScheme="blue" onClick={onConfirm} ml={3}>
              I Understand
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

export function IssueModalInputFields({ issue }: { issue: IssueDetail }) {
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose: onCloseDisclosure } = useDisclosure();
  const {
    isOpen: isBulkConfirmOpen,
    onOpen: onBulkConfirmOpen,
    onClose: onBulkConfirmClose,
  } = useDisclosure();
  const {
    isOpen: isIndividualConfirmOpen,
    onOpen: onIndividualConfirmOpen,
    onClose: onIndividualConfirmClose,
  } = useDisclosure();
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [newSenderName, setNewSenderName] = useState<string>("");
  const [loadingTemplateId, setLoadingTemplateId] = useState<string | null>(null);
  const [bulkUpdateSenderId, setBulkUpdateSenderId] = useState<string | null>(null);
  const [bulkUpdateSenderName, setBulkUpdateSenderName] = useState<string>("");
  const [messagesCount, setMessagesCount] = useState<number>(0);
  const [pendingIndividualUpdate, setPendingIndividualUpdate] = useState<{
    template: MessageTemplate;
    senderId: string | null;
    senderName: string;
    messagesCount: number;
  } | null>(null);

  const { data: user } = useUserDetailQuery(issue?.context?.userId, {
    enabled: !!issue?.context?.userId,
    staleTime: 0,
  });

  const { data: currentUser } = useCurrentUserQuery();

  // Query message templates with this sender
  const { data: templates = [], isLoading, refetch } = useMessageTemplateListQuery(
    user?.id ? `sender_id=${user.id}` : "",
    {
      enabled: !!user?.id,
    },
  );

  const onClose = () => {
    // Refetch templates when modal closes to update the list
    refetch();
    onCloseDisclosure();
  };

  const onBulkConfirmCloseAndRefetch = () => {
    refetch();
    onBulkConfirmClose();
  };

  const handleBulkUpdate = async (senderId: string | null) => {
    if (!user?.id) return;

    const senderName = senderId ? currentUser?.fullName || "you" : "Camino Bot";

    // Count messages for all templates with this sender
    const queryString = createQueryString({
      filters: [
        { fieldName: "sender_id", value: user.id, operator: "=" },
        { fieldName: "status", value: ["delivered", "removed"], operator: "not_in" },
      ],
    });

    try {
      const messages = await apiClient.get(`/api/messages?${queryString}`);

      const count = Array.isArray(messages) ? messages.length : 0;

      setBulkUpdateSenderId(senderId);
      setBulkUpdateSenderName(senderName);
      setMessagesCount(count);
      onBulkConfirmOpen();
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to count messages",
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleUpdateSender = async (template: MessageTemplate, senderId: string | null) => {
    const senderName = senderId ? currentUser?.fullName || "you" : "Camino Bot";

    // Count pending messages for this template
    const queryString = createQueryString({
      filters: [
        { fieldName: "template_id", value: template.id, operator: "=" },
        { fieldName: "status", value: ["delivered", "removed"], operator: "not_in" },
      ],
    });

    try {
      const messages = await apiClient.get(`/api/messages?${queryString}`);
      const count = Array.isArray(messages) ? messages.length : 0;

      // Show confirmation alert
      setPendingIndividualUpdate({
        template,
        senderId,
        senderName,
        messagesCount: count,
      });
      onIndividualConfirmOpen();
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to count messages",
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleConfirmIndividualUpdate = async () => {
    if (!pendingIndividualUpdate) return;

    const { template, senderId, senderName } = pendingIndividualUpdate;

    setLoadingTemplateId(template.id);
    onIndividualConfirmClose();

    try {
      // Update template sender
      await apiClient.put(apiMessageTemplateById(template.id), {
        messageTemplate: { senderId },
      });

      // Always show modal
      setSelectedTemplate(template);
      setNewSenderName(senderName);
      onOpen();

      // Don't refetch here - wait until modal closes
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to update template",
        status: "error",
        duration: 5000,
      });
    } finally {
      setLoadingTemplateId(null);
      setPendingIndividualUpdate(null);
    }
  };

  const handleViewTemplate = (template: MessageTemplate) => {
    const path = generatePath(PEOPLE_TEAM_ROUTES.messageTemplates.show, { id: template.id });
    window.open(path, "_blank");
  };

  const handleViewMessages = (template: MessageTemplate) => {
    navigate(`${PEOPLE_TEAM_ROUTES.messages.index}?templateId=${template.id}`);
  };

  if (isLoading) {
    return <Text>Loading templates...</Text>;
  }

  if (templates.length === 0) {
    return (
      <Text>
        {user?.fullName || "This user"}
        {" "}
        was previously a sender for message templates but no longer has any active templates. This issue has been resolved.
      </Text>
    );
  }

  return (
    <>
      <VStack align="stretch" spacing={4}>
        <Text>
          {user?.fullName || "This user"}
          {"'s "}
          account has been deactivated. Below are all message templates with this sender.
        </Text>

        <Box p={3} borderWidth="1px" borderRadius="md" borderColor="blue.200" bg="blue.50">
          <VStack align="stretch" spacing={2}>
            <Text fontWeight="medium">Bulk Update All Templates</Text>
            <HStack spacing={2}>
              <GrayTableButton
                size="sm"
                onClick={() => handleBulkUpdate(null)}
              >
                Update all to send from Camino
              </GrayTableButton>
              <GrayTableButton
                size="sm"
                onClick={() => handleBulkUpdate(currentUser?.id || null)}
                isDisabled={!currentUser}
              >
                Update all to send from me
              </GrayTableButton>
            </HStack>
          </VStack>
        </Box>

        <VStack align="stretch" spacing={2}>
          {templates.map((template) => (
            <Box
              key={template.id}
              p={3}
              borderWidth="1px"
              borderRadius="md"
              borderColor="gray.200"
            >
              <VStack align="stretch" spacing={3}>
                <HStack justify="space-between" align="center">
                  <Link
                    onClick={() => handleViewTemplate(template)}
                    color="blue.500"
                    cursor="pointer"
                    fontWeight="medium"
                  >
                    {template.name}
                  </Link>
                </HStack>

                <HStack spacing={2} flexWrap="wrap">
                  <GrayTableButton
                    size="sm"
                    onClick={() => handleUpdateSender(template, null)}
                    isLoading={loadingTemplateId === template.id}
                    isDisabled={loadingTemplateId !== null}
                  >
                    Update sender to bot
                  </GrayTableButton>
                  <GrayTableButton
                    size="sm"
                    onClick={() => handleUpdateSender(template, currentUser?.id || null)}
                    isLoading={loadingTemplateId === template.id}
                    isDisabled={!currentUser || loadingTemplateId !== null}
                  >
                    Update sender to me
                  </GrayTableButton>
                  <GrayTableButton
                    size="sm"
                    onClick={() => handleViewMessages(template)}
                    isDisabled={loadingTemplateId !== null}
                  >
                    View messages
                  </GrayTableButton>
                </HStack>
              </VStack>
            </Box>
          ))}
        </VStack>
      </VStack>

      {user && (
        <BulkUpdateConfirmationModal
          userId={user.id}
          newSenderId={bulkUpdateSenderId}
          newSenderName={bulkUpdateSenderName}
          templatesCount={templates.length}
          messagesCount={messagesCount}
          isOpen={isBulkConfirmOpen}
          onClose={onBulkConfirmCloseAndRefetch}
        />
      )}

      {pendingIndividualUpdate && (
        <IndividualUpdateConfirmationAlert
          template={pendingIndividualUpdate.template}
          senderId={pendingIndividualUpdate.senderId}
          senderName={pendingIndividualUpdate.senderName}
          messagesCount={pendingIndividualUpdate.messagesCount}
          isOpen={isIndividualConfirmOpen}
          onClose={onIndividualConfirmClose}
          onConfirm={handleConfirmIndividualUpdate}
        />
      )}

      {selectedTemplate && (
        <FailedMessagesModal
          template={selectedTemplate}
          isOpen={isOpen}
          onClose={onClose}
          newSenderName={newSenderName}
        />
      )}
    </>
  );
}

export function deactivatedSenderResolution(issue: IssueDetail): ResolutionInfo {
  const { user } = issue || {};
  return ({
    issueType: "deactivated_sender",
    tagText: deactivatedSenderIssueType(issue),
    title: user?.fullName ? `Message Sender ${user?.fullName} Deactivated` : "Message Sender Deactivated",
    additionalContext: `${user?.firstName || "The sender"} has been deactivated and cannot send messages. Update the templates below.`,
    inputComponent: IssueModalInputFields,
    modalSize: "3xl",
  });
}
